import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, Power, ShieldCheck } from "lucide-react";
import api, { errorText } from "../../../lib/api";
import { useAuth } from "../../../context/AuthContext";
import { ConfirmDialog } from "./ConfirmDialog";
import { LinkBox } from "./LinkBox";
import { ROLE_LABEL } from "./InviteDialog";

const RANK = { customer: 0, partner: 1, staff: 2, admin: 3, owner: 4 };
const MANAGED = "account management";

// What the confirm step says for each change (everything here is written to the admin audit).
export const confirmFor = (a, p) => {
  if (p.status === "disabled") {
    return { title: `Disable ${a.email}?`, body: "They can't sign in until the account is enabled again. All their sessions end at once.", confirm: "Disable", danger: true };
  }
  if (p.status === "active") return { title: `Enable ${a.email}?`, body: "They can sign in again with their password.", confirm: "Enable" };
  if (p.link) {
    return { title: a.status === "invited" ? "Make a new invite link?" : "Make a password reset link?",
      body: "A new single-use link is made and every older unused link stops working. Nothing is emailed: you copy it and send it yourself.", confirm: "Make link" };
  }
  if (p.role === a.role) {
    return { title: `Manage this ${ROLE_LABEL[a.role]} role here?`, confirm: "Manage here",
      body: `${a.email} stays ${ROLE_LABEL[a.role]}. From now on only account management changes it: ADMIN_EMAILS and the start-up sync leave it alone.` };
  }
  const down = RANK[p.role] < RANK[a.role];
  const body = down ? "This is a demotion: all their sessions end at once."
    : p.role === "admin" ? "Admins need 2-step sign-in: their sessions without it end now; the admin area opens after the 2-step setup."
      : "They keep their ducks and spending history.";
  return { title: `${ROLE_LABEL[a.role]} → ${ROLE_LABEL[p.role]} for ${a.email}?`, body, confirm: "Change role", danger: down };
};

const whyNot = (d, me) => (d.account.id === me?.id ? "This is you. Your own account is changed in Account settings, not here."
  : (RANK[me?.role] ?? 0) < RANK.admin ? "View only: staff can look, admins make the changes."
    : "Only the owner can change admin and owner accounts.");

const Btn = ({ testid, onClick, disabled, title, icon: Icon, danger, children }) => (
  <button type="button" data-testid={testid} onClick={onClick} disabled={disabled} title={title}
    className={`flex h-8 items-center gap-1.5 rounded-full border px-3 text-[12px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${danger ? "border-rose-300 text-rose-700 hover:bg-rose-50" : "border-border hover:bg-muted"}`}>
    <Icon className="h-3.5 w-3.5" /> {children}
  </button>
);

export const AccountActions = ({ d, onChanged }) => {
  const { user } = useAuth();
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [link, setLink] = useState(null);
  const a = d.account;
  if (!d.can_manage) return <p data-testid="account-view-only" className="mt-3 rounded-xl bg-muted/60 px-3 py-2 text-[12px] text-muted-foreground">{whyNot(d, user)}</p>;
  const run = async () => {
    setBusy(true);
    setError("");
    try {
      if (pending.link) {
        const { data } = await api.post(`/accounts/${a.id}/reset`, { public_base: window.location.origin });
        setLink(data);
      } else {
        await api.patch(`/accounts/${a.id}`, pending.role ? { role: pending.role } : { status: pending.status });
        toast.success("Saved · written to the admin audit");
      }
      onChanged();
    } catch (e) {
      setError(errorText(e, "Could not save this change."));
    } finally {
      setBusy(false);
      setPending(null);
    }
  };
  const options = d.assignable.includes(a.role) ? d.assignable : [a.role, ...d.assignable];
  const disabled = a.status === "disabled";
  return (
    <div data-testid="account-actions" className="mt-3 space-y-2">
      <div className="flex flex-wrap items-center gap-1.5">
        <select data-testid="account-role-select" aria-label="Role" value={a.role} onChange={(e) => setPending({ role: e.target.value })}
          className="h-8 rounded-full border border-input bg-background px-2.5 text-[12px] font-semibold">
          {options.map((r) => <option key={r} value={r} disabled={!d.assignable.includes(r)}>{ROLE_LABEL[r]}</option>)}
        </select>
        {a.role_source !== MANAGED && d.assignable.includes(a.role) && (
          <Btn testid="account-adopt-role" icon={ShieldCheck} onClick={() => setPending({ role: a.role })}>Manage role here</Btn>
        )}
        <Btn testid="account-toggle-status" icon={Power} danger={!disabled} onClick={() => setPending({ status: disabled ? "active" : "disabled" })}>
          {disabled ? "Enable" : "Disable"}
        </Btn>
        <Btn testid="account-reset-link" icon={KeyRound} disabled={disabled} title={disabled ? "Enable the account first" : undefined}
          onClick={() => setPending({ link: true })}>
          {a.status === "invited" ? "New invite link" : "Password reset link"}
        </Btn>
      </div>
      {error && <p data-testid="account-action-error" className="rounded-lg bg-rose-50 px-2.5 py-1.5 text-[12px] font-medium text-rose-700">{error}</p>}
      {link && <LinkBox result={link} testid="account-link" />}
      <ConfirmDialog open={!!pending} busy={busy} onConfirm={run} onClose={() => setPending(null)} {...(pending ? confirmFor(a, pending) : {})} />
    </div>
  );
};
